import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import logo from "../assets/images/logo.png";
import SearchInput from "../components/SearchInput";
import { handleLogin, handleLogout } from "../utils/userauth";
import { useAuth } from "../context/AuthContext";
import AlertMessage, { AlertType } from "../components/AlertMessage";

// ✅ Import all icons as a module
import * as FaIcons from "react-icons/fa";

// ✅ Cast icons explicitly to React.FC so TS treats them as valid JSX elements
const FaUser = FaIcons.FaUser as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaHome = FaIcons.FaHome as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaChartBar = FaIcons.FaChartBar as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaStore = FaIcons.FaStore as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaBox = FaIcons.FaBox as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaShoppingCart = FaIcons.FaShoppingCart as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaUserTie = FaIcons.FaUserTie as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaSignOutAlt = FaIcons.FaSignOutAlt as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaSignInAlt = FaIcons.FaSignInAlt as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const FaGift = FaIcons.FaGift as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface MainNavbarProps {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  isLoggedIn: boolean;
  showSubnav: boolean;
  setShowSubnav: (v: boolean) => void;
  userId: string;
  disableSearch?: boolean;
}

const MainNavbar: React.FC<MainNavbarProps> = ({
  searchQuery,
  setSearchQuery,
  isLoggedIn,
  showSubnav,
  setShowSubnav,
  userId,
  disableSearch = false,
}) => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout, currentUser } = useAuth();

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<AlertType>("info");

  const showAlert = (msg: string, type: AlertType = "info") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(null), 3000);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSubnav(false);
      }
    };

    if (showSubnav) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSubnav, setShowSubnav]);

  return (
    <nav className="main-navbar">
      <div className="nav-container">
        {/* Logo */}
        <div className="logo" onClick={() => navigate("/")}>
          <img src={logo} alt="E-Commerce Logo" className="logo-image" />
        </div>

        {/* Search Bar */}
        {!disableSearch && (
          <div className="search-bar">
            <SearchInput
              searchText={searchQuery}
              setSearchText={setSearchQuery}
            />
          </div>
        )}

        {/* Profile / Dropdown */}
        <div
          className="auth-profile"
          ref={dropdownRef}
          onClick={() => setShowSubnav(!showSubnav)}
        >
          <FaUser className="profile-icon_nav_bar" />

          {showSubnav && (
            <div className="dropdown-menu">
              <div
                className="dropdown-item"
                onClick={() => {
                  setShowSubnav(false);
                  navigate(`/`);
                }}
              >
                <FaHome className="icon_nav_bar" /> Home
              </div>

              {isLoggedIn ? (
                <>
                  {currentUser?.role === "admin" && (
                    <div
                      className="dropdown-item"
                      onClick={() => {
                        setShowSubnav(false);
                        navigate(`/admin/dashboard`);
                      }}
                    >
                      <FaChartBar className="icon_nav_bar" /> Admin Dashboard
                    </div>
                  )}

                  {currentUser?.role === "vendor" && (
                    <div
                      className="dropdown-item"
                      onClick={() => {
                        setShowSubnav(false);
                        navigate(`/vendor/dashboard`);
                      }}
                    >
                      <FaStore className="icon_nav_bar" /> Vendor Dashboard
                    </div>
                  )}

                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setShowSubnav(false);
                      navigate(`/user/trackorders/${userId}`);
                    }}
                  >
                    <FaBox className="icon_nav_bar" /> My Orders
                  </div>


                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setShowSubnav(false);
                      navigate(`/user/cart/${userId}`);
                    }}
                  >
                    <FaShoppingCart className="icon_nav_bar" /> My Cart
                  </div>

                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setShowSubnav(false);
                      navigate(`/user/rewards/${userId}`);
                    }}
                  >
                    <FaGift className="icon_nav_bar" /> Rewards
                  </div>

                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setShowSubnav(false);
                      navigate(`/user/userprofile/${userId}`);
                    }}
                  >
                    <FaUserTie className="icon_nav_bar" /> My Profile
                  </div>

                  <div
                    className="dropdown-item logout"
                    onClick={() => {
                      handleLogout(logout, showAlert, setShowSubnav);
                    }}
                  >
                    <FaSignOutAlt className="icon_nav_bar" /> Logout
                  </div>
                </>
              ) : (
                <div
                  className="dropdown-item"
                  onClick={() => handleLogin(navigate)}
                >
                  <FaSignInAlt className="icon_nav_bar" /> Login
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {alertMessage && (
        <AlertMessage message={alertMessage} type={alertType} />
      )}
    </nav>
  );
};

export default MainNavbar;
